import { CircularProgress, Grid, Button, Typography, Stack, TextField } from '@material-ui/core';
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router';
import Carousel from 'nuka-carousel';
import Page from '../components/Page';
import { userAtom } from 'src/atoms/user';
import { useRecoilValue } from 'recoil';
import Image from '../components/Image';
import { useQuery } from 'react-query';
import { useTheme } from '@material-ui/core/styles';
import { boxStyling, loaderStyle } from 'src/utils/styles';
import AmountDetails from 'src/components/admin/Request/AmountDetails';
import ViolatedPolicies from 'src/components/admin/Request/ViolatedPolicies';
import RequestHeader from 'src/components/admin/Request/RequestHeader';
import { checkRequest, resolveRequest } from 'src/services/admin.service';
import ConfirmationDialogue from 'src/components/ConfirmationDialogue';
import EmployeeRequestPage from './EmployeeRequestPage';
import axios from 'axios';
import PulseLoader from 'src/components/loaders/PulseLoader';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';

const images = {
  dr_prescription: 'Prescriptions',
  medical_bill: 'Bills',
  analysis_rays_request: 'Reports'
};

const AdminRequestPage = ({ edit, preview, reqId }) => {
  const theme = useTheme();
  const { id } = useParams();
  const user = useRecoilValue(userAtom);
  // const [remaining, setRemaining] = useState(0);
  const divRef = React.useRef();

  const {
    data,
    isLoading: loading,
    isError,
    error,
    refetch: refetchCheckRequest
  } = useQuery(['request_check', reqId || id], async () => await checkRequest(reqId || id));

  useEffect(() => {
    if (divRef.current && edit) divRef.current.scrollIntoView({ behavior: 'smooth' });
  });
  // console.log(checkData.data);
  const [showImages, setShowImages] = useState(false);
  const [amountEGP, setAmountEGP] = useState();
  const [amountUSD, setAmountUSD] = useState();
  const [rate, setRate] = useState(data?.rate);
  const [query_amount, setQuery_amount] = useState(amountUSD);

  useDebounceEffect(
    () => {
      setQuery_amount(amountUSD);
      setBypassedPolicies([]);
    },
    1000,
    [amountUSD]
  );

  const policiesQuery = useQuery(
    ['violated_policies', query_amount],
    async () =>
      await axios
        .get(`/admin/violated-policies/${reqId || id}?amount=${query_amount}`)
        .then((res) => res.data.violatedPolicies),
    {
      enabled: !!data && query_amount !== undefined && query_amount !== null,
      refetchOnWindowFocus: false,
      cacheTime: 0
    }
  );

  const { data: violatedPolicies, isLoading: violatedPoliciesLoading } = policiesQuery;
  const [bypassedPolicies, setBypassedPolicies] = useState([]);
  const [adminComments, setAdminComments] = useState('');

  const handleBypassPolicy = (policyId) => {
    if (bypassedPolicies.includes(policyId)) {
      setBypassedPolicies((prev) => prev.filter((id) => id !== policyId));
    } else {
      setBypassedPolicies((prev) => [...prev, policyId]);
    }
  };

  useEffect(() => {
    if (data) {
      const isAdmin = data?.request.status.includes('ADMIN');
      setAmountEGP(
        isAdmin && data?.request.deserved_amount_egp === 0
          ? data?.totalToBePaidEGP
          : data?.request.deserved_amount_egp
      );
      setAmountUSD(
        isAdmin && data?.request.deserved_amount === 0
          ? data?.totalToBePaidUSD
          : data?.request.deserved_amount_egp /
              (data?.rate || data?.request.deserved_amount_egp / data?.request.deserved_amount)
      );
      setRate(
        isAdmin ? data?.rate : data?.request.deserved_amount_egp / data?.request.deserved_amount
      );
      setAdminComments(data?.request.admin_comments || '');
    }
  }, [data]);

  const handleAmountChange = (type, value) => {
    if (type === 'EGP') {
      setAmountEGP(value);
      setAmountUSD(value / rate);
    } else if (type === 'USD') {
      setAmountUSD(value);
      setAmountEGP(value * rate);
    } else if (type === 'rate') {
      setRate(value);
      setAmountUSD(amountEGP / value);
    }
  };

  const [confirmationState, setConfirmationState] = useState({
    open: false,
    state: ''
  });

  const [confirmationError, setConfirmationError] = useState({
    isError: false,
    error: ''
  });

  const confirm = useCallback(() => {
    if (
      (confirmationState.state === 'disapprove' || confirmationState.state === 'reopen') &&
      adminComments === ''
    ) {
      setConfirmationError({
        isError: true,
        error: 'Please provide a comment'
      });
    } else {
      const requestId = reqId || id;
      const requestBody = {
        comments: adminComments,
        violated_policies: bypassedPolicies.map((policy) => policy.name).join(', '),
        deserved_amount_egp: Number(amountEGP),
        deserved_amount_usd: Number(amountUSD),
        admin_reviewed: false
      };

      switch (confirmationState.state) {
        case 'approve':
          requestBody.status = 'APPROVED';
          break;
        case 'send to finance':
          requestBody.status = 'PENDING_FINANCE_REVIEW';
          break;
        case 'pending':
          requestBody.status = 'PENDING_ADMIN_REVIEW';
          requestBody.admin_reviewed = true;
          break;
        case 'reopen':
          requestBody.status = 'PENDING_ADMIN_REVIEW';
          requestBody.admin_reviewed = false;
          break;
        default:
          requestBody.status = 'DISAPPROVED';
          const { violatedPolicies } = data;
          requestBody.violated_policies = violatedPolicies.map((policy) => policy.name).join(', ');
          requestBody.deserved_amount_egp = 0;
          requestBody.deserved_amount_usd = 0;
          break;
      }
      return resolveRequest({
        id: requestId,
        body: {
          ...requestBody
        }
      });
    }
  }, [
    confirmationState.state,
    adminComments,
    reqId,
    id,
    amountEGP,
    amountUSD,
    bypassedPolicies,
    data
  ]);

  if (loading) {
    return <CircularProgress style={loaderStyle} />;
  } else if (isError) {
    return <Typography variant="h4">Error: {error}</Typography>;
  }
  if (
    data?.request?.employee?.id === user?.id &&
    data?.request?.status !== 'PENDING_ADMIN_REVIEW'
  ) {
    return <EmployeeRequestPage preview={preview} reqId={reqId} />;
  }
  return (
    <Page title={'Admin Requests | Incorta Care'}>
      <ConfirmationDialogue
        isError={confirmationError.isError}
        errorMessage={confirmationError.error}
        open={confirmationState.open}
        setOpen={(state) => setConfirmationState((prev) => ({ ...prev, open: state }))}
        queryId={''}
        redirectUrl={'/dashboard/all-requests'}
        onConfirm={confirm}
        loadingMessage={'Please wait while we process your request'}
        successMessage={
          confirmationState.state === 'approve'
            ? 'Request has been approved successfully'
            : confirmationState.state === 'reopen'
            ? 'Request has been reopened successfully'
            : confirmationState.state === 'send to finance'
            ? 'Request has been sent to finance successfully'
            : confirmationState.state === 'pending'
            ? 'Request has been saved successfully'
            : 'Request has been disapproved successfully'
        }
        confirmationMessage={`Are you sure you want to ${
          confirmationState.state === 'pending' ? 'save' : confirmationState.state
        } this request?`}
      />
      <Grid container direction="column" spacing={0} style={{ padding: '12px' }}>
        {data.request.status !== 'PENDING_ADMIN_REVIEW' ? (
          <Typography variant="subtitle1" color={theme.palette.incorta.main}>
            This request is not pending admin review
          </Typography>
        ) : null}

        <RequestHeader
          data={data}
          usd={Number(amountUSD)}
          showBalance={true}
          showHistory={!preview}
        />

        <div style={boxStyling}>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="h4">Receipts, Prescriptions and Reports</Typography>
            <Button onClick={() => setShowImages((state) => !state)}>
              {showImages ? 'Hide All' : 'Show All'}
            </Button>
          </Stack>
          <div hidden={!showImages}>
            {Object.entries(images).map((arr) => {
              return (
                <Grid
                  key={arr[0]}
                  direction="column"
                  container
                  style={{
                    marginTop: '20px'
                  }}
                >
                  <Typography variant="h4">{arr[1]}</Typography>
                  {data.request[arr[0]].length === 0 ? (
                    <Typography>NA </Typography>
                  ) : (
                    <Carousel height="30vh">
                      {data.request[arr[0]].map((imgKey) => {
                        return <Image key={imgKey} imgKey={imgKey} height={'30vh'} />;
                      })}
                    </Carousel>
                  )}
                </Grid>
              );
            })}
          </div>
        </div>
        <Stack gap={2} direction={'row'}>
          <Grid direction="column" container item style={boxStyling} xs={12} md={6}>
            <Typography variant="h4">Employee Notes</Typography>
            <Typography variant="body1" gutterBottom paddingLeft={1}>
              {data.request.notes || 'NA'}
            </Typography>
          </Grid>
          <Grid
            direction="column"
            container
            key={'Doctor Comments'}
            style={boxStyling}
            item
            xs={12}
            md={6}
          >
            <Typography variant="h4">{'Doctor Comments'}</Typography>
            <Typography variant="body1" gutterBottom paddingLeft={1}>
              {data.request['dr_comments'] || 'NA'}
            </Typography>
          </Grid>
        </Stack>

        {/* Admin Area */}
        <Stack container gap={2} direction={'row'}>
          <Grid item xs={12} md={8} container direction={'column'}>
            <AmountDetails
              percentage_covered={data.percentage_covered}
              usd={Number(amountUSD)}
              egp={Number(amountEGP)}
              rate={Number(rate)}
              onChangeAmount={handleAmountChange}
              viewOnly={data.request.status !== 'PENDING_ADMIN_REVIEW'}
            />
            <div style={boxStyling}>
              <Typography variant="h4" marginBottom={theme.spacing(2)}>
                Your Comment
              </Typography>
              <TextField
                label={
                  data.request.status === 'PENDING_ADMIN_REVIEW'
                    ? 'Comments'
                    : ['PENDING_FINANCE_REVIEW', 'APPROVED', 'DISAPPROVED'].includes(
                        data.request.status
                      )
                    ? 'Admin Comments'
                    : 'You can edit comments when the request is the pending admin status'
                }
                multiline
                rows={4}
                viewOnly={data.request.status !== 'PENDING_ADMIN_REVIEW'}
                disabled={data.request.status !== 'PENDING_ADMIN_REVIEW'}
                fullWidth
                onChange={(e) => setAdminComments(e.target.value)}
                value={
                  data.request.status === 'PENDING_ADMIN_REVIEW'
                    ? adminComments
                    : data.request.admin_comments || 'No Comments Provided'
                }
              />
            </div>
          </Grid>

          <Grid item xs={12} md={4} style={{ ...boxStyling }}>
            {violatedPoliciesLoading || !violatedPolicies ? (
              <Stack
                height={'100%'}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <PulseLoader />
              </Stack>
            ) : (
              <ViolatedPolicies
                id={reqId || id}
                amount={Number(amountUSD)}
                policies={violatedPolicies}
                onBypassPolicy={handleBypassPolicy}
                bypassedPolicies={bypassedPolicies}
                viewOnly={data.request.status !== 'PENDING_ADMIN_REVIEW'}
              />
            )}
          </Grid>
        </Stack>
        {violatedPolicies &&
        bypassedPolicies.length !== violatedPolicies.length &&
        data.request.status === 'PENDING_ADMIN_REVIEW' ? (
          <Typography
            variant="subtitle2"
            style={{ marginTop: '10px' }}
            color={'error.dark'}
            align="right"
          >
            You must bypass all violated policies to approve the request
          </Typography>
        ) : null}
        <Stack
          direction={'row'}
          justifyContent={'flex-end'}
          style={{
            display: data.request.status !== 'PENDING_ADMIN_REVIEW' || preview ? 'none' : 'flex'
          }}
        >
          <Button
            variant="contained"
            color="incorta"
            style={{ marginTop: '20px' }}
            onClick={() => {
              setConfirmationError({
                isError: false,
                error: ''
              });
              setConfirmationState({ open: true, state: 'pending' });
            }}
            size="large"
          >
            Save
          </Button>
          {/*<Button*/}
          {/*  variant="contained"*/}
          {/*  color="incorta"*/}
          {/*  style={{ marginTop: '20px', marginLeft: '20px' }}*/}
          {/*  onClick={() => {*/}
          {/*    setConfirmationError({*/}
          {/*      isError: false,*/}
          {/*      error: ''*/}
          {/*    });*/}
          {/*    setConfirmationState({ open: true, state: 'approve' });*/}
          {/*  }}*/}
          {/*  size="large"*/}
          {/*  disabled={*/}
          {/*    (violatedPolicies && bypassedPolicies.length !== violatedPolicies.length) ||*/}
          {/*    data.request.status !== 'PENDING_ADMIN_REVIEW' ||*/}
          {/*    Number(amountEGP) < 0 ||*/}
          {/*    Number(amountUSD) < 0 ||*/}
          {/*    amountUSD !== query_amount ||*/}
          {/*    violatedPoliciesLoading*/}
          {/*  }*/}
          {/*>*/}
          {/*  Approve*/}
          {/*</Button>*/}
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '20px', marginLeft: '20px' }}
            onClick={() => {
              setConfirmationError({
                isError: false,
                error: ''
              });
              setConfirmationState({ open: true, state: 'send to finance' });
            }}
            disabled={
              (violatedPolicies && bypassedPolicies.length !== violatedPolicies.length) ||
              data.request.status !== 'PENDING_ADMIN_REVIEW' ||
              Number(amountEGP) < 0 ||
              Number(amountUSD) < 0 ||
              amountUSD !== query_amount ||
              violatedPoliciesLoading
            }
          >
            Send to Finance
          </Button>
          <Button
            variant="contained"
            color="error"
            style={{ marginTop: '20px', marginLeft: '20px' }}
            onClick={() => {
              setConfirmationError({
                isError: false,
                error: ''
              });
              setConfirmationState({ open: true, state: 'disapprove' });
            }}
            disabled={data.request.status !== 'PENDING_ADMIN_REVIEW'}
          >
            Reject
          </Button>
        </Stack>
        <Stack
          direction={'row'}
          justifyContent={'flex-end'}
          style={{
            display: data.request.status === 'DISAPPROVED' && !preview ? 'flex' : 'none'
          }}
        >
          <Button
            variant="contained"
            color="warning"
            style={{ marginTop: '20px' }}
            onClick={() => {
              setConfirmationError({
                isError: false,
                error: ''
              });
              setConfirmationState({ open: true, state: 'reopen' });
            }}
            size="large"
          >
            Reopen
          </Button>
        </Stack>
        <div ref={divRef} style={{ visibility: 'hidden', height: '1px' }}>
          Just to scroll
        </div>
      </Grid>
    </Page>
  );
};

export default AdminRequestPage;
